import React, { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { formatPhone } from '../../utilities/formatPhone';
import { useBodyScrollLock } from '../../utilities/useBodyScrollLock';
import UnsavedChangesModal from '../../utilities/UnsavedChangesModal';
import { writePortalAuditEvent, PORTAL_AUDIT } from '../../utilities/portalAuditLog';

const functions = getFunctions();
const createEmployee = httpsCallable(functions, 'createEmployee');
const sendConfirmationCode = httpsCallable(functions, 'sendConfirmationCode');
const verifyConfirmationCode = httpsCallable(functions, 'verifyConfirmationCode');
const updateEmployeeProfile = httpsCallable(functions, 'updateEmployeeProfile');
const updateEmployeePermissions = httpsCallable(functions, 'updateEmployeePermissions');
const setEmployeePayRate = httpsCallable(functions, 'setEmployeePayRate');
const getEmployeePayRates = httpsCallable(functions, 'getEmployeePayRates');
const setEmployeeCommissionRate = httpsCallable(functions, 'setEmployeeCommissionRate');
const getEmployeeCommissionRates = httpsCallable(functions, 'getEmployeeCommissionRates');
const getCommissionSettings = httpsCallable(functions, 'getCommissionSettings');
const terminateEmployeeFn = httpsCallable(functions, 'terminateEmployee');

function downloadCSV(rows, filename) {
  const lines = rows.map(r =>
    r.map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(',')
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const ROLES     = ['employee', 'manager', 'owner'];
const POSITIONS = ['Server', 'Host', 'Cook', 'Bartender', 'Dishwasher', 'Expo', 'Manager', 'Owner'];
const DEPTS     = ['FOH', 'BOH', 'Management'];

const ROLE_BADGE = {
  owner:    'bg-amber-100 text-amber-800 border-amber-200',
  manager:  'bg-blue-100 text-blue-700 border-blue-200',
  employee: 'bg-neutral-100 text-neutral-600 border-neutral-200',
};

// ─── Employee Directory ───────────────────────────────────────────────────────

const EmployeeDirectory = ({ user, restaurantId }) => {
  const [employees, setEmployees]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [editEmp, setEditEmp]             = useState(null);
  const [showTerminated, setShowTerminated] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState(null);

  const isOwner      = user?.role === 'owner';
  const canManage    = isOwner || user?.permissions?.manageEmployees;
  const canAdd       = isOwner || user?.permissions?.canAddEmployees;
  const canTerminate = isOwner || user?.permissions?.canTerminateEmployees;

  const activeEmps     = employees.filter(e => e.isActive !== false);
  const terminatedEmps = employees.filter(e => e.isActive === false);
  const displayedEmps  = showTerminated ? employees : activeEmps;

  useEffect(() => {
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'employees'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [restaurantId]);

  if (loading) {
    return <p className="text-sm text-neutral-400 text-center py-12">Loading employees…</p>;
  }

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-neutral-500">{activeEmps.length} active staff</p>
          {terminatedEmps.length > 0 && (
            <button
              onClick={() => setShowTerminated(p => !p)}
              className={`text-sm px-2.5 py-1 rounded-full border transition-colors ${showTerminated ? 'bg-red-50 text-red-700 border-red-200' : 'text-neutral-400 border-neutral-200 hover:border-neutral-300'}`}
            >
              {showTerminated ? 'Hide' : 'Show'} terminated ({terminatedEmps.length})
            </button>
          )}
        </div>
        {canAdd && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors"
          >
            + Add Employee
          </button>
        )}
      </div>

      {/* Employee list */}
      {activeEmps.length === 0 && !showTerminated ? (
        <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-neutral-200 rounded-xl">
          <p className="text-sm font-medium text-neutral-400">No employees yet</p>
          {canAdd && (
            <p className="text-sm text-neutral-300 mt-1">Click "Add Employee" to get started.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {displayedEmps.map(emp => {
            const displayName = emp.firstName
              ? `${emp.firstName} ${emp.lastName}`
              : emp.name || '—';
            const isTerminated     = emp.isActive === false;
            const stripeReady      = emp.stripePayoutsEnabled === true;
            const stripeIncomplete = !!emp.stripeAccountId && !stripeReady;
            const hasPortal = !!emp.portalAccountId;

            return (
              <div key={emp.id} className={`border rounded-xl p-4 flex flex-col gap-2 ${isTerminated ? 'bg-neutral-50 border-neutral-200 opacity-75' : 'bg-white border-neutral-200'}`}>
                {/* Name + role */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-neutral-800 leading-tight">{displayName}</p>
                    {emp.position && (
                      <p className="text-sm text-neutral-400 mt-0.5">{emp.position}{emp.department ? ` · ${emp.department}` : ''}</p>
                    )}
                  </div>
                  <span className={`shrink-0 text-sm font-medium px-2 py-0.5 rounded-full border capitalize ${ROLE_BADGE[emp.role] || ROLE_BADGE.employee}`}>
                    {emp.role}
                  </span>
                </div>

                {/* Contact */}
                {emp.email && (
                  <p className="text-sm text-neutral-500 truncate">{emp.email}</p>
                )}
                {emp.phone && (
                  <div className="flex items-center gap-3">
                    <a
                      href={`tel:${emp.phone.replace(/\D/g, '')}`}
                      className="text-sm text-neutral-500 hover:text-neutral-800 transition-colors"
                    >
                      {formatPhone(emp.phone)}
                    </a>
                    <a
                      href={`sms:${emp.phone.replace(/\D/g, '')}`}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Text
                    </a>
                  </div>
                )}

                {/* Pay */}
                <p className="text-sm text-neutral-400">
                  {emp.payType === 'salary'
                    ? `$${(emp.annualSalary || 0).toLocaleString()} / yr`
                    : `$${(emp.hourlyRate || 0).toFixed(2)} / hr`}
                </p>

                {/* Status pills */}
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {isTerminated ? (
                    <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-red-50 text-red-600 border-red-200">
                      Terminated
                    </span>
                  ) : (
                    <>
                      {emp.onBreak && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-amber-50 text-amber-700 border-amber-200">
                          On Break
                        </span>
                      )}
                      {emp.clockedIn && !emp.onBreak && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-green-50 text-green-700 border-green-200">
                          Clocked In
                        </span>
                      )}
                      <span className={`text-sm px-2 py-0.5 rounded-full border font-medium ${hasPortal ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-neutral-50 text-neutral-400 border-neutral-200'}`}>
                        {hasPortal ? 'Portal ✓' : 'No portal'}
                      </span>
                      {stripeReady && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-emerald-50 text-emerald-700 border-emerald-200">
                          Stripe ✓
                        </span>
                      )}
                      {stripeIncomplete && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-amber-50 text-amber-700 border-amber-200">
                          Stripe Incomplete
                        </span>
                      )}
                      {!emp.stripeAccountId && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-neutral-50 text-neutral-400 border-neutral-200">
                          No Stripe
                        </span>
                      )}
                      {hasPortal && emp.accountSetupComplete === false && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-amber-50 text-amber-700 border-amber-200">
                          Setup Pending
                        </span>
                      )}
                      {!emp.position && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-amber-50 text-amber-700 border-amber-200">
                          No Position
                        </span>
                      )}
                      {!emp.email && (
                        <span className="text-sm px-2 py-0.5 rounded-full border font-medium bg-amber-50 text-amber-700 border-amber-200">
                          No Email
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Action buttons / termination record */}
                {isTerminated ? (
                  <div className="mt-1 border-t border-neutral-100 pt-2 space-y-0.5">
                    {emp.terminationDate && (
                      <p className="text-xs text-neutral-400">
                        {new Date(emp.terminationDate.seconds ? emp.terminationDate.seconds * 1000 : emp.terminationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {emp.terminationType ? ` · ${emp.terminationType.replace('_', ' ')}` : ''}
                      </p>
                    )}
                    {emp.rehireEligible === false && (
                      <p className="text-xs text-red-400">Not eligible for rehire</p>
                    )}
                    {emp.finalPayProcessed === false && (
                      <p className="text-xs text-amber-500">Final pay not yet confirmed</p>
                    )}
                    <button
                      onClick={() => {
                        const ts = emp.terminationDate?.seconds
                          ? new Date(emp.terminationDate.seconds * 1000)
                          : emp.terminationDate ? new Date(emp.terminationDate) : null;
                        const lastDay = emp.lastDayWorked || '';
                        const name = emp.firstName ? `${emp.firstName} ${emp.lastName}` : emp.name || '';
                        downloadCSV([
                          ['Field', 'Value'],
                          ['Employee Name', name],
                          ['Role', emp.role || ''],
                          ['Position', emp.position || ''],
                          ['Department', emp.department || ''],
                          ['Email', emp.email || ''],
                          ['Phone', emp.phone || ''],
                          ['Hire Date', emp.hireDate?.seconds ? new Date(emp.hireDate.seconds * 1000).toLocaleDateString() : emp.hireDate || ''],
                          ['Last Day Worked', lastDay],
                          ['Termination Date', ts ? ts.toLocaleDateString() : ''],
                          ['Termination Type', emp.terminationType || ''],
                          ['Termination Note', emp.terminationNote || ''],
                          ['Terminated By', emp.terminatedBy || ''],
                          ['Rehire Eligible', emp.rehireEligible === false ? 'No' : 'Yes'],
                          ['Rehire Note', emp.rehireNote || ''],
                          ['Final Pay Issued', emp.finalPayProcessed ? 'Yes' : 'No'],
                          ['PTO Payout', emp.ptoPayout > 0 ? `$${emp.ptoPayout.toFixed(2)}` : '$0.00'],
                        ], `termination_record_${(name).replace(/\s+/g, '_').toLowerCase()}.csv`);
                      }}
                      className="w-full text-xs py-1 border border-neutral-200 rounded-lg text-neutral-400 hover:bg-neutral-50 transition-colors mt-1"
                    >
                      Download Record
                    </button>
                  </div>
                ) : (
                  <>
                    {isOwner && (
                      <button
                        onClick={() => setEditEmp(emp)}
                        className="mt-1 w-full text-sm py-1.5 border border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-500 hover:text-neutral-800 transition-colors"
                      >
                        Edit Role &amp; Pay
                      </button>
                    )}
                    {canTerminate && emp.id !== user?.employeeId && (
                      <button
                        onClick={() => setTerminateTarget(emp)}
                        className="w-full text-sm py-1.5 border border-red-200 rounded-lg text-red-500 hover:bg-red-50 hover:border-red-300 transition-colors"
                      >
                        Terminate
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Owner: edit role & pay modal */}
      {editEmp && (
        <EditEmployeeModal
          emp={editEmp}
          restaurantId={restaurantId}
          user={user}
          onClose={() => setEditEmp(null)}
          onTerminate={canTerminate && editEmp?.id !== user?.employeeId ? (emp) => setTerminateTarget(emp) : null}
        />
      )}

      {/* Terminate employee modal */}
      {terminateTarget && (
        <TerminateModal
          emp={terminateTarget}
          restaurantId={restaurantId}
          user={user}
          onClose={() => setTerminateTarget(null)}
          onSuccess={() => { setTerminateTarget(null); setEditEmp(null); }}
        />
      )}

      {/* Add employee modal */}
      {showModal && (
        <AddEmployeeModal
          restaurantId={restaurantId}
          user={user}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

// ─── Add Employee Modal ───────────────────────────────────────────────────────

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '',
  username: '',
  role: 'employee', position: '', department: '',
  payType: 'hourly', hourlyRate: '', annualSalary: '',
};

const AddEmployeeModal = ({ restaurantId, user, onClose }) => {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [step, setStep]           = useState('form'); // 'form' | 'phone' | 'done'
  const [doneInfo, setDoneInfo]   = useState(null);   // { username }
  const [error, setError]         = useState('');

  // Phone verification state
  const [codeSent, setCodeSent]         = useState(false);
  const [codeInput, setCodeInput]       = useState('');
  const [sendingCode, setSendingCode]   = useState(false);
  const [verifying, setVerifying]       = useState(false);
  const [phoneError, setPhoneError]     = useState('');

  useBodyScrollLock();

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // ── Step 1: validate form → advance to phone step ──────────────────────────
  const handleNext = () => {
    if (!form.firstName || !form.lastName || !form.email) {
      return setError('First name, last name, and email are required.');
    }
    if (!form.phone) {
      return setError('A phone number is required for verification.');
    }
    setError('');
    setCodeSent(false);
    setCodeInput('');
    setPhoneError('');
    setStep('phone');
  };

  // ── Step 2a: send verification code ───────────────────────────────────────
  const handleSendCode = async () => {
    setSendingCode(true);
    setPhoneError('');
    try {
      await sendConfirmationCode({ phoneNumber: form.phone, restaurantId });
      setCodeSent(true);
      setCodeInput('');
    } catch (err) {
      setPhoneError(err.message || 'Failed to send code. Try again.');
    } finally {
      setSendingCode(false);
    }
  };

  // ── Step 2b: verify code → create employee ─────────────────────────────────
  const handleVerifyAndCreate = async () => {
    if (!codeInput || codeInput.length < 6) {
      return setPhoneError('Enter the 6-digit code.');
    }
    setVerifying(true);
    setPhoneError('');
    try {
      await verifyConfirmationCode({ phoneNumber: form.phone, code: codeInput, restaurantId });

      // Code valid — create the employee
      const result = await createEmployee({
        restaurantId,
        firstName:    form.firstName.trim(),
        lastName:     form.lastName.trim(),
        email:        form.email.trim(),
        phone:        form.phone.trim(),
        username:     form.username.trim(),
        role:         form.role,
        position:     form.position,
        department:   form.department,
        payType:      form.payType,
        hourlyRate:   form.payType === 'hourly'  ? parseFloat(form.hourlyRate)  || 0 : 0,
        annualSalary: form.payType === 'salary'  ? parseFloat(form.annualSalary) || 0 : 0,
      });

      const resolvedUsername = form.username.trim() ||
        `${form.firstName.trim().toLowerCase()}.${form.lastName.trim().toLowerCase()}`;
      setDoneInfo({ username: resolvedUsername, employeeId: result.data.employeeId });
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.EMPLOYEE_CREATED, {
        targetEmployeeId:   result.data.employeeId || '',
        targetEmployeeName: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        role:               form.role,
      });
      setStep('done');
    } catch (err) {
      setPhoneError(err.message || 'Verification failed. Try again.');
    } finally {
      setVerifying(false);
    }
  };

  // ── Step labels ────────────────────────────────────────────────────────────
  const stepLabel = step === 'form' ? 'Step 1 of 2 — Info' : step === 'phone' ? 'Step 2 of 2 — Verify Phone' : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90dvh]">

        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-neutral-800">Add Employee</h3>
            {stepLabel && <p className="text-xs text-neutral-400 mt-0.5">{stepLabel}</p>}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-lg leading-none">✕</button>
        </div>

        {/* ── Done ── */}
        {step === 'done' && (
          <div className="px-6 py-10 text-center">
            <div className="text-3xl mb-3">✓</div>
            <p className="text-base font-semibold text-neutral-800 mb-1">Employee created!</p>
            <p className="text-sm text-neutral-500 mt-1">
              Portal username: <span className="font-mono font-semibold text-neutral-700">{doneInfo?.username}</span>
            </p>
            <p className="text-sm text-neutral-400 mt-1">A welcome email has been sent with their temporary credentials.</p>
            <button onClick={onClose} className="mt-6 px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg">
              Done
            </button>
          </div>
        )}

        {/* ── Step 1: Info form ── */}
        {step === 'form' && (
          <>
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">

              <Section label="Personal Info">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name *" value={form.firstName} onChange={v => set('firstName', v)} />
                  <Field label="Last Name *"  value={form.lastName}  onChange={v => set('lastName', v)} />
                </div>
                <Field label="Email *" type="email" value={form.email} onChange={v => set('email', v)} />
                <Field label="Phone *" type="tel" value={form.phone} onChange={v => set('phone', formatPhone(v))} placeholder="___-___-____" maxLength={12} />
                <p className="text-xs text-neutral-400 -mt-1">Required — used to verify the employee's identity via SMS.</p>
              </Section>

              <Section label="Role & Position">
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Role</label>
                  <div className="flex gap-2">
                    {ROLES.map(r => (
                      <button key={r} type="button" onClick={() => set('role', r)}
                        className={`flex-1 py-1.5 text-sm font-medium rounded-lg border capitalize transition-colors ${form.role === r ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600'}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">Position</label>
                    <select value={form.position} onChange={e => set('position', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                      <option value="">— none —</option>
                      {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">Department</label>
                    <select value={form.department} onChange={e => set('department', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                      <option value="">— none —</option>
                      {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
              </Section>

              <Section label="Pay">
                <div className="flex gap-2">
                  {['hourly', 'salary'].map(t => (
                    <button key={t} type="button" onClick={() => set('payType', t)}
                      className={`flex-1 py-1.5 text-sm font-medium rounded-lg border capitalize transition-colors ${form.payType === t ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600'}`}>
                      {t}
                    </button>
                  ))}
                </div>
                {form.payType === 'hourly' ? (
                  <Field label="Hourly Rate ($)" type="number" value={form.hourlyRate}
                    onChange={v => set('hourlyRate', v)} placeholder="0.00" />
                ) : (
                  <Field label="Annual Salary ($)" type="number" value={form.annualSalary}
                    onChange={v => set('annualSalary', v)} placeholder="0" />
                )}
              </Section>

              <Section label="Portal Login">
                <Field label="Username (leave blank to auto-generate)" value={form.username}
                  onChange={v => set('username', v)} placeholder="firstname.lastname" />
                <p className="text-xs text-neutral-400 -mt-1">A temporary password will be auto-generated and sent via email.</p>
              </Section>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2 shrink-0">
              <button onClick={onClose} className="px-4 py-2 text-sm text-neutral-500 hover:text-neutral-700">
                Cancel
              </button>
              <button onClick={handleNext}
                className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors">
                Next: Verify Phone →
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Phone verification ── */}
        {step === 'phone' && (
          <>
            <div className="px-6 py-6 space-y-5 overflow-y-auto flex-1">

              {/* Info block */}
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3">
                <p className="text-sm font-medium text-neutral-700">
                  Sending to: <span className="font-semibold">{form.phone}</span>
                </p>
                <button
                  onClick={() => { setStep('form'); setCodeSent(false); setCodeInput(''); setPhoneError(''); }}
                  className="text-xs text-brand-blue hover:underline mt-0.5"
                >
                  ← Change number
                </button>
              </div>

              {!codeSent ? (
                <>
                  <p className="text-sm text-neutral-500">
                    We'll send a 6-digit code to this number to verify it belongs to the employee before creating their account.
                  </p>
                  <button
                    onClick={handleSendCode}
                    disabled={sendingCode}
                    className="w-full py-3 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                  >
                    {sendingCode ? 'Sending…' : 'Send Verification Code'}
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm text-neutral-600 mb-3">
                      Code sent! Enter the 6-digit code texted to {form.phone}.
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={codeInput}
                      onChange={e => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="— — — — — —"
                      className="w-full text-center text-2xl font-mono tracking-[0.5em] px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400"
                    />
                  </div>

                  <button
                    onClick={handleVerifyAndCreate}
                    disabled={verifying || codeInput.length < 6}
                    className="w-full py-3 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                  >
                    {verifying ? 'Verifying…' : 'Verify & Create Account'}
                  </button>

                  <button
                    onClick={handleSendCode}
                    disabled={sendingCode}
                    className="w-full py-2 text-sm text-neutral-400 hover:text-neutral-600 disabled:opacity-50 transition-colors"
                  >
                    {sendingCode ? 'Resending…' : 'Resend code'}
                  </button>
                </>
              )}

              {phoneError && <p className="text-sm text-red-600">{phoneError}</p>}
            </div>

            <div className="px-6 py-4 border-t border-neutral-100 shrink-0">
              <button onClick={() => { setStep('form'); setCodeSent(false); setCodeInput(''); setPhoneError(''); }}
                className="text-sm text-neutral-500 hover:text-neutral-700">
                ← Back
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

// ─── Edit Employee Modal (owner only) ────────────────────────────────────────

const EDIT_TABS = ['Profile', 'Pay Rates', 'Commission', 'Permissions'];

const EditEmployeeModal = ({ emp, restaurantId, user, onClose, onTerminate }) => {
  useBodyScrollLock();
  const [tab, setTab]           = useState('Profile');
  const [role, setRole]         = useState(emp.role || 'employee');
  const [position, setPosition] = useState(emp.position || '');
  const [department, setDept]   = useState(emp.department || '');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [saved, setSaved]       = useState(false);

  // Unsaved changes guard
  const [pendingAction, setPendingAction] = useState(null); // tab name or 'close'
  const [guardSaving, setGuardSaving]     = useState(false);
  const permsDirtyRef = useRef(false);
  const permsSaveFnRef = useRef(null);

  const handlePermsDirtyChange = (isDirty, saveFn) => {
    permsDirtyRef.current  = isDirty;
    permsSaveFnRef.current = isDirty ? saveFn : null;
  };

  const displayName = emp.firstName ? `${emp.firstName} ${emp.lastName}` : emp.name || '—';

  // Profile tab is dirty when role/position/dept differs from original and hasn't been saved yet
  const profileDirty = !saved && (
    role !== (emp.role || 'employee') ||
    position !== (emp.position || '') ||
    department !== (emp.department || '')
  );

  const currentTabDirty = () => {
    if (tab === 'Profile') return profileDirty;
    if (tab === 'Permissions') return permsDirtyRef.current;
    return false;
  };

  const handleTabClick = (t) => {
    if (t === tab) return;
    if (currentTabDirty()) {
      setPendingAction(t);
    } else {
      setTab(t);
    }
  };

  const handleCloseClick = () => {
    if (currentTabDirty()) {
      setPendingAction('close');
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateEmployeeProfile({
        restaurantId,
        employeeId: emp.id,
        role,
        position,
        department,
      });
      setSaved(true);
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.EMPLOYEE_UPDATED, {
        targetEmployeeId:   emp.id,
        targetEmployeeName: displayName,
        role,
        position,
        department,
      });
    } catch (err) {
      setError(err.message || 'Failed to save. Try again.');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const currentSaveFn = () => {
    if (tab === 'Profile') return handleSave;
    if (tab === 'Permissions') return permsSaveFnRef.current;
    return null;
  };

  const handleGuardSave = async () => {
    const saveFn = currentSaveFn();
    if (!saveFn) { doAction(); return; }
    setGuardSaving(true);
    try {
      await saveFn();
      permsDirtyRef.current = false;
      doAction();
    } catch (_) { /* keep modal open */ }
    finally { setGuardSaving(false); }
  };

  const handleGuardLeave = () => {
    permsDirtyRef.current = false;
    doAction();
  };

  const doAction = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action === 'close') {
      onClose();
    } else if (action) {
      setTab(action);
    }
  };

  const handleGuardStay = () => setPendingAction(null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[90dvh]">

        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-neutral-800">Edit Employee</h3>
            <p className="text-sm text-neutral-400 mt-0.5">{displayName}</p>
          </div>
          <button onClick={handleCloseClick} className="text-neutral-400 hover:text-neutral-600 text-lg leading-none">✕</button>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-neutral-100 shrink-0">
          {EDIT_TABS.map(t => (
            <button key={t} onClick={() => handleTabClick(t)}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${tab === t ? 'text-neutral-900 border-b-2 border-neutral-900' : 'text-neutral-400 hover:text-neutral-600'}`}>
              {t}
            </button>
          ))}
        </div>

        {/* Guard modal for this drilldown */}
        {pendingAction !== null && (
          <UnsavedChangesModal
            saving={guardSaving}
            onSave={handleGuardSave}
            onLeave={handleGuardLeave}
            onStay={handleGuardStay}
          />
        )}

        {/* Tab: Profile */}
        {tab === 'Profile' && (
          saved ? (
            <div className="px-6 py-8 text-center">
              <p className="text-sm font-semibold text-neutral-700 mb-1">Changes saved!</p>
              <p className="text-sm text-neutral-400">Portal permissions updated to match new role.</p>
              <button onClick={onClose} className="mt-4 px-5 py-2 text-sm bg-neutral-900 text-white rounded-lg">Close</button>
            </div>
          ) : (
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Role</label>
                <div className="flex gap-2">
                  {ROLES.map(r => (
                    <button key={r} type="button" onClick={() => setRole(r)}
                      className={`flex-1 py-1.5 text-sm font-medium rounded-lg border capitalize transition-colors ${role === r ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600'}`}>
                      {r}
                    </button>
                  ))}
                </div>
                {role !== emp.role && (
                  <p className="text-sm text-amber-600 mt-1">Changing role resets portal permissions to defaults.</p>
                )}
              </div>

              {/* Position + Dept */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Position</label>
                  <select value={position} onChange={e => setPosition(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                    <option value="">— none —</option>
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Department</label>
                  <select value={department} onChange={e => setDept(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                    <option value="">— none —</option>
                    {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={handleCloseClick} className="px-4 py-2 text-sm text-neutral-500 hover:text-neutral-700">Cancel</button>
                <button onClick={handleSave} disabled={saving}
                  className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors">
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>

              {/* Danger zone */}
              {onTerminate && (
                <div className="border-t border-red-100 pt-4 mt-2">
                  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">Danger Zone</p>
                  <button
                    type="button"
                    onClick={() => onTerminate(emp)}
                    className="w-full py-2 text-sm font-medium border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Terminate Employee
                  </button>
                </div>
              )}
            </div>
          )
        )}

        {/* Tab: Pay Rates */}
        {tab === 'Pay Rates' && (
          <PayHistoryTab
            restaurantId={restaurantId}
            employeeId={emp.id}
            displayName={displayName}
            emp={emp}
          />
        )}

        {/* Tab: Commission */}
        {tab === 'Commission' && (
          <div className="overflow-y-auto flex-1">
            <CommissionTab
              restaurantId={restaurantId}
              employeeId={emp.id}
              displayName={displayName}
            />
          </div>
        )}

        {/* Tab: Permissions */}
        {tab === 'Permissions' && (
          <PermissionsTab
            emp={emp}
            restaurantId={restaurantId}
            user={user}
            onDirtyChange={handlePermsDirtyChange}
          />
        )}
      </div>
    </div>
  );
};

// ─── Pay History Tab ──────────────────────────────────────────────────────────

const today = new Date().toISOString().slice(0, 10);

const PayHistoryTab = ({ restaurantId, employeeId, displayName, emp }) => {
  const [rates, setRates]         = useState(null); // null = loading
  const [showForm, setShowForm]   = useState(false);
  const [rateType, setRateType]   = useState('hourly');
  const [amount, setAmount]       = useState('');
  const [effectiveStart, setEffectiveStart] = useState(today);
  const [reason, setReason]       = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const loadRates = async () => {
    setRates(null);
    try {
      const res = await getEmployeePayRates({ restaurantId, employeeId });
      setRates(res.data.rates || []);
    } catch {
      setRates([]);
    }
  };

  useEffect(() => { loadRates(); }, [restaurantId, employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetRate = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0) return setError('Enter a valid amount.');
    if (!effectiveStart) return setError('Effective date is required.');
    setSaving(true);
    setError('');
    try {
      await setEmployeePayRate({
        restaurantId,
        employeeId,
        rateType,
        amount: n,
        effectiveStart,
        reason: reason.trim() || null,
      });
      setAmount('');
      setReason('');
      setShowForm(false);
      loadRates();
    } catch (err) {
      setError(err.message || 'Failed to set rate.');
    } finally {
      setSaving(false);
    }
  };

  const formatRate = (r) => {
    if (r.rateType === 'salary') return `$${Number(r.amount).toLocaleString()} / yr`;
    return `$${Number(r.amount).toFixed(2)} / hr`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-6 py-4 flex items-center justify-between shrink-0 border-b border-neutral-50">
        <p className="text-sm text-neutral-500">{displayName} — pay rate history</p>
        <button
          onClick={() => { setShowForm(v => !v); setError(''); }}
          className="text-sm font-semibold text-neutral-700 hover:text-neutral-900 px-3 py-1.5 border border-neutral-300 rounded-lg transition-colors"
        >
          {showForm ? 'Cancel' : '+ Set New Rate'}
        </button>
      </div>

      {/* Set new rate form */}
      {showForm && (
        <div className="px-6 py-4 border-b border-neutral-100 space-y-3 shrink-0 bg-neutral-50">
          <div className="flex gap-2">
            {['hourly', 'salary'].map(t => (
              <button key={t} type="button" onClick={() => setRateType(t)}
                className={`flex-1 py-1.5 text-sm font-medium rounded-lg border capitalize transition-colors ${rateType === t ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600'}`}>
                {t}
              </button>
            ))}
          </div>
          <Field
            label={rateType === 'hourly' ? 'Hourly Rate ($)' : 'Annual Salary ($)'}
            type="number" value={amount} onChange={setAmount} placeholder="0.00"
          />
          <Field label="Effective Date" type="date" value={effectiveStart} onChange={setEffectiveStart} />
          <Field label="Reason (optional)" value={reason} onChange={setReason} placeholder="e.g. raise, promotion" />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button onClick={handleSetRate} disabled={saving}
            className="w-full py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors">
            {saving ? 'Saving…' : 'Save New Rate'}
          </button>
        </div>
      )}

      {/* Rate history list */}
      <div className="overflow-y-auto flex-1 px-6 py-3 space-y-2">
        {rates === null ? (
          <p className="text-sm text-neutral-400 text-center py-6">Loading…</p>
        ) : rates.length === 0 ? (
          // No subcollection entries yet — show the rate set at creation from the employee doc
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-neutral-800">
                {emp?.payType === 'salary'
                  ? `$${Number(emp.annualSalary || 0).toLocaleString()} / yr`
                  : `$${Number(emp.hourlyRate || 0).toFixed(2)} / hr`}
              </span>
              <span className="text-sm font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Current</span>
            </div>
            <p className="text-sm text-neutral-500 mt-1">Set at hire — no rate changes on record</p>
          </div>
        ) : (
          rates.map((r, i) => (
            <div key={r.id} className={`rounded-lg border px-4 py-3 ${i === 0 ? 'border-emerald-200 bg-emerald-50' : 'border-neutral-200 bg-white'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-neutral-800">{formatRate(r)}</span>
                {i === 0 && (
                  <span className="text-sm font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Current</span>
                )}
              </div>
              <p className="text-sm text-neutral-500 mt-1">
                Effective {formatDate(r.effectiveStart)}
                {r.effectiveEnd ? ` → ${formatDate(r.effectiveEnd)}` : ' → present'}
              </p>
              {r.reason && <p className="text-sm text-neutral-400 mt-0.5 italic">{r.reason}</p>}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Commission Tab ───────────────────────────────────────────────────────────

const todayISO = new Date().toISOString().slice(0, 10);
const APPLIES_TO_OPTS = [
  { value: 'reservation_total', label: 'Reservation Total' },
  { value: 'food_only',         label: 'Food Only' },
  { value: 'beverage_only',     label: 'Beverage Only' },
];

const CommissionTab = ({ restaurantId, employeeId, displayName }) => {
  const [rates, setRates]               = useState(null);
  const [commSettings, setCommSettings] = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');

  // Form state
  const [mode, setMode]                         = useState('none');
  const [type, setType]                         = useState('percent');
  const [rate, setRate]                         = useState('');
  const [appliesTo, setAppliesTo]               = useState('reservation_total');
  const [positionId, setPositionId]             = useState('');
  const [tierId, setTierId]                     = useState('');
  const [pointsOverride, setPointsOverride]     = useState('');
  const [effectiveStart, setEffectiveStart]     = useState(todayISO);
  const [reason, setReason]                     = useState('');

  const load = async () => {
    setRates(null);
    try {
      const [ratesRes, settingsRes] = await Promise.all([
        getEmployeeCommissionRates({ restaurantId, employeeId }),
        getCommissionSettings({ restaurantId }),
      ]);
      setRates(ratesRes.data.rates || []);
      setCommSettings(settingsRes.data.settings || {});
    } catch {
      setRates([]);
      setCommSettings({});
    }
  };

  useEffect(() => { load(); }, [restaurantId, employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentRate = rates && rates.length > 0 ? rates[0] : null;

  // When position changes reset tier
  const handlePositionChange = (pid) => {
    setPositionId(pid);
    setTierId('');
  };

  const selectedPosition = (commSettings?.positions || []).find(p => p.id === positionId);
  const selectedTier = (selectedPosition?.tiers || []).find(t => t.id === tierId);

  // Derived pool total points across all positions (for showing resulting %)
  const allTierPoints = (commSettings?.positions || [])
    .flatMap(p => p.tiers)
    .reduce((sum, t) => sum + (Number(t.points) || 0), 0);

  const effectivePts = pointsOverride !== '' && !isNaN(parseFloat(pointsOverride))
    ? parseFloat(pointsOverride)
    : (selectedTier ? Number(selectedTier.points) : 0);

  const resultingPct = allTierPoints > 0 && effectivePts > 0
    ? ((effectivePts / allTierPoints) * 100).toFixed(1)
    : null;

  const handleSave = async () => {
    if (!effectiveStart) return setError('Effective date is required.');
    setSaving(true);
    setError('');
    try {
      await setEmployeeCommissionRate({
        restaurantId,
        employeeId,
        mode,
        // individual
        type: mode === 'individual' ? type : undefined,
        rate: mode === 'individual' ? (parseFloat(rate) || 0) : undefined,
        appliesTo: mode === 'individual' ? appliesTo : undefined,
        // pooled
        positionId: mode === 'pooled' ? positionId : undefined,
        tierId: mode === 'pooled' ? tierId : undefined,
        pointsOverride: mode === 'pooled' && pointsOverride !== ''
          ? parseFloat(pointsOverride) : null,
        effectiveStart,
        reason: reason.trim() || null,
      });
      setShowForm(false);
      setReason('');
      setPointsOverride('');
      load();
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const formatRate = (r) => {
    if (r.mode === 'none') return 'No commission';
    if (r.mode === 'individual') {
      if (r.type === 'percent') return `${r.rate}% of ${r.appliesTo?.replace('_', ' ')}`;
      return `$${Number(r.rate).toFixed(2)} flat per reservation`;
    }
    if (r.mode === 'pooled') {
      const pos = (commSettings?.positions || []).find(p => p.id === r.positionId);
      const tier = (pos?.tiers || []).find(t => t.id === r.tierId);
      const pts = r.pointsOverride != null ? r.pointsOverride : tier?.points;
      return `Pool — ${pos?.name || r.positionId} / ${tier?.name || r.tierId} (${pts ?? '?'} pts)`;
    }
    return '—';
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
  };

  const modeColor = {
    none: 'bg-neutral-100 text-neutral-500 border-neutral-200',
    individual: 'bg-blue-50 text-blue-700 border-blue-200',
    pooled: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  return (
    <div>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-neutral-100">
        <div>
          <p className="text-sm text-neutral-500">{displayName}</p>
          {currentRate && (
            <span className={`inline-block mt-1 text-sm font-medium px-2 py-0.5 rounded-full border capitalize ${modeColor[currentRate.mode] || modeColor.none}`}>
              {currentRate.mode}
            </span>
          )}
        </div>
        <button
          onClick={() => { setShowForm(v => !v); setError(''); }}
          className="text-sm font-semibold text-neutral-700 hover:text-neutral-900 px-3 py-1.5 border border-neutral-300 rounded-lg transition-colors"
        >
          {showForm ? 'Cancel' : '+ Set Commission'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="px-6 py-4 border-b border-neutral-100 space-y-3 bg-neutral-50">
          {/* Mode */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Mode</label>
            <div className="flex gap-2">
              {['none', 'individual', 'pooled'].map(m => (
                <button key={m} type="button" onClick={() => setMode(m)}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-lg border capitalize transition-colors ${mode === m ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600'}`}>
                  {m}
                </button>
              ))}
            </div>
            <p className="text-sm text-neutral-400 mt-1">
              {mode === 'none' && 'No commission earned.'}
              {mode === 'individual' && 'Earns commission on their own reservations at their own rate.'}
              {mode === 'pooled' && 'Share of the restaurant pool based on position-tier points.'}
            </p>
          </div>

          {/* Individual fields */}
          {mode === 'individual' && (
            <>
              <div className="flex gap-2">
                {['percent', 'flat'].map(t => (
                  <button key={t} type="button" onClick={() => setType(t)}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-lg border capitalize transition-colors ${type === t ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-600'}`}>
                    {t === 'percent' ? '% of Sale' : 'Flat Rate'}
                  </button>
                ))}
              </div>
              <Field
                label={type === 'percent' ? 'Commission %' : 'Flat Amount ($)'}
                type="number" value={rate} onChange={setRate}
                placeholder={type === 'percent' ? '5' : '10.00'}
              />
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Applies To</label>
                <select value={appliesTo} onChange={e => setAppliesTo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                  {APPLIES_TO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </>
          )}

          {/* Pooled fields */}
          {mode === 'pooled' && (
            <>
              {(!commSettings?.positions || commSettings.positions.length === 0) ? (
                <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  No commission positions defined. Set them up in Commission Settings first.
                </p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">Position</label>
                    <select value={positionId} onChange={e => handlePositionChange(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                      <option value="">— select position —</option>
                      {commSettings.positions.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  {selectedPosition && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-500 mb-1">Tier</label>
                      <select value={tierId} onChange={e => setTierId(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                        <option value="">— select tier —</option>
                        {selectedPosition.tiers.map(t => {
                          const pct = allTierPoints > 0
                            ? ((Number(t.points) / allTierPoints) * 100).toFixed(1) : '—';
                          return (
                            <option key={t.id} value={t.id}>
                              {t.name} ({t.points} pts ≈ {pct}%)
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  <Field
                    label={`Points Override (optional — tier default: ${selectedTier?.points ?? '—'})`}
                    type="number" value={pointsOverride} onChange={setPointsOverride}
                    placeholder={String(selectedTier?.points ?? '')}
                  />
                  {resultingPct && (
                    <p className="text-sm text-emerald-600 font-medium">
                      Resulting pool share: ~{resultingPct}%
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {/* Common */}
          <Field label="Effective Date" type="date" value={effectiveStart} onChange={setEffectiveStart} />
          <Field label="Reason (optional)" value={reason} onChange={setReason} placeholder="e.g. promotion, new hire" />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button onClick={handleSave} disabled={saving}
            className="w-full py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors">
            {saving ? 'Saving…' : 'Save Commission Config'}
          </button>
        </div>
      )}

      {/* History */}
      <div className="px-6 py-3 space-y-2">
        {rates === null ? (
          <p className="text-sm text-neutral-400 text-center py-6">Loading…</p>
        ) : rates.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-6">No commission history yet.</p>
        ) : (
          rates.map((r, i) => (
            <div key={r.id} className={`rounded-lg border px-4 py-3 ${i === 0 ? 'border-violet-200 bg-violet-50' : 'border-neutral-200 bg-white'}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-neutral-800">{formatRate(r)}</p>
                  <p className="text-sm text-neutral-500 mt-0.5">
                    {formatDate(r.effectiveStart)}
                    {r.effectiveEnd ? ` → ${formatDate(r.effectiveEnd)}` : ' → present'}
                  </p>
                  {r.reason && <p className="text-sm text-neutral-400 italic mt-0.5">{r.reason}</p>}
                </div>
                {i === 0 && (
                  <span className={`shrink-0 text-sm font-medium px-2 py-0.5 rounded-full border capitalize ${modeColor[r.mode] || modeColor.none}`}>
                    {r.mode}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Small helpers ────────────────────────────────────────────────────────────

const Section = ({ label, children }) => (
  <div>
    <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2">{label}</p>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder = '', maxLength }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-500 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
    />
  </div>
);

// ─── Permissions Tab ─────────────────────────────────────────────────────────

const PORTAL_PERMISSIONS = [
  { key: 'manageEmployees',     label: 'Manage Employees',     desc: 'View employee profiles and edit role, pay, and permissions' },
  { key: 'canAddEmployees',     label: 'Add Employees',        desc: 'Create new employee accounts and send portal invites' },
  { key: 'canTerminateEmployees', label: 'Terminate Employees', desc: 'Process staff terminations and revoke portal/POS access' },
  { key: 'manageMenu',          label: 'Manage Menus',         desc: 'Add, edit, and remove menu items' },
  { key: 'manageHours',       label: 'Manage Hours',        desc: 'Edit regular hours, special hours, and holidays' },
  // TODO: not yet enforced — revisit
  // { key: 'manageSchedule',    label: 'Manage Schedule',     desc: 'Build and publish shift schedules' },
  { key: 'editSchedule',      label: 'Edit Schedule',       desc: 'Modify already-published schedules' },
  { key: 'manageReservations',label: 'Manage Reservations', desc: 'Override and cancel any reservation' },
  { key: 'blockTables',       label: 'Block Tables',        desc: 'Mark tables as unavailable for reservations' },
  { key: 'viewPayroll',       label: 'View Payroll',        desc: 'See pay period summaries and ledger entries' },
  // TODO: not yet enforced — revisit
  // { key: 'editPayroll',       label: 'Edit Payroll',        desc: 'Lock periods, run payroll, add adjustments' },
  { key: 'viewAnalytics',     label: 'View Analytics',      desc: 'Revenue trends and performance metrics' },
  { key: 'viewReports',       label: 'View Reports',        desc: 'Cash drawer logs, tip summaries, shift reports' },
  { key: 'EmailOnLowInventory', label: 'Low Inventory Alerts', desc: 'Receive email when any ingredient drops to or below its minimum threshold' },
];

const POS_PERMISSIONS = [
  { key: 'canComp',             label: 'Comp Items',       desc: 'Comp menu items at the POS terminal' },
  { key: 'canRefund',           label: 'Issue Refunds',    desc: 'Process refunds at the POS terminal' },
  { key: 'canAdjustTips',       label: 'Adjust Tips',      desc: 'Enter and edit tip amounts on any closed check at the POS terminal (servers can always adjust their own checks)' },
  // TODO: not yet enforced — revisit
  // { key: 'canEditHours',        label: 'Edit Hours',       desc: 'Adjust clock-in / clock-out times' },
  { key: 'canMoveReservations', label: 'Move Reservations', desc: 'Reassign a seated reservation to a different table on the floor map' },
  { key: 'canEditTables',       label: 'Edit Tables',      desc: 'Access the Edit Tables screen on POS and floor map edit mode in the portal' },
  // TODO: not yet enforced — revisit
  // { key: 'isAdmin',             label: 'POS Admin',        desc: 'Full admin access on the POS terminal' },
];

const PermissionsTab = ({ emp, restaurantId, user, onDirtyChange }) => {
  const [perms, setPerms]   = useState({ ...(emp.permissions || {}) });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState('');
  const [dirty, setDirty]   = useState(false);
  const saveFnRef = useRef(null);

  const toggle = (key) => {
    setPerms(p => ({ ...p, [key]: !p[key] }));
    setSaved(false);
    if (!dirty) {
      setDirty(true);
      onDirtyChange?.(true, () => saveFnRef.current?.());
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateEmployeePermissions({ restaurantId, employeeId: emp.id, permissions: perms });
      setSaved(true);
      setDirty(false);
      onDirtyChange?.(false, null);
      const displayName = emp.firstName ? `${emp.firstName} ${emp.lastName || ''}`.trim() : emp.name || '';
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.EMPLOYEE_PERMISSIONS_CHANGED, {
        targetEmployeeId:   emp.id,
        targetEmployeeName: displayName,
        permissions:        { ...perms },
      });
    } catch (err) {
      setError(err.message || 'Failed to save permissions.');
      throw err;
    } finally {
      setSaving(false);
    }
  };
  saveFnRef.current = handleSave;

  const Toggle = ({ permKey }) => (
    <button
      type="button"
      onClick={() => toggle(permKey)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full shrink-0 transition-colors ${perms[permKey] ? 'bg-neutral-900' : 'bg-neutral-200'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${perms[permKey] ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

        <div>
          <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">Portal Access</p>
          <div className="space-y-2.5">
            {PORTAL_PERMISSIONS.map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-neutral-800">{label}</p>
                  <p className="text-sm text-neutral-400">{desc}</p>
                </div>
                <Toggle permKey={key} />
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-neutral-100 pt-5">
          <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">POS Terminal</p>
          <div className="space-y-2.5">
            {POS_PERMISSIONS.map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-neutral-800">{label}</p>
                  <p className="text-sm text-neutral-400">{desc}</p>
                </div>
                <Toggle permKey={key} />
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-between shrink-0">
        {saved && <span className="text-sm text-emerald-600 font-medium">Saved!</span>}
        {!saved && <span className="text-sm text-neutral-400">Changes apply on next login.</span>}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Permissions'}
        </button>
      </div>
    </div>
  );
};

// ─── Terminate Employee Modal ─────────────────────────────────────────────────

const TERMINATION_TYPES = [
  { value: 'voluntary',    label: 'Voluntary Resignation' },
  { value: 'involuntary',  label: 'Involuntary (Fired)' },
  { value: 'layoff',       label: 'Layoff' },
  { value: 'retirement',   label: 'Retirement' },
  { value: 'contract_end', label: 'Contract End' },
  { value: 'other',        label: 'Other' },
];

const TerminateModal = ({ emp, restaurantId, user, onClose, onSuccess }) => {
  useBodyScrollLock();
  const displayName = emp.firstName ? `${emp.firstName} ${emp.lastName}` : emp.name || '—';
  const [step, setStep]     = useState('form'); // 'form' | 'confirm'
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    terminationType:    'voluntary',
    lastDayWorked:      todayISO,
    terminationNote:    '',
    rehireEligible:     true,
    rehireNote:         '',
    finalPayProcessed:  false,
    ptoPayout:          '',
  });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      await terminateEmployeeFn({
        restaurantId,
        employeeId:        emp.id,
        terminationType:   form.terminationType,
        terminationNote:   form.terminationNote,
        lastDayWorked:     form.lastDayWorked || null,
        rehireEligible:    form.rehireEligible,
        rehireNote:        form.rehireNote,
        finalPayProcessed: form.finalPayProcessed,
        ptoPayout:         parseFloat(form.ptoPayout) || 0,
      });
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.EMPLOYEE_DELETED, {
        targetEmployeeId:   emp.id,
        targetEmployeeName: displayName,
        terminationType:    form.terminationType,
      });
      onSuccess();
    } catch (err) {
      setError(err.message || 'Termination failed. Try again.');
      setStep('form');
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ field }) => (
    <button
      type="button"
      onClick={() => set(field, !form[field])}
      className={`relative inline-flex h-5 w-9 items-center rounded-full shrink-0 transition-colors ${form[field] ? 'bg-neutral-900' : 'bg-neutral-200'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form[field] ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 px-4 py-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90dvh]">

        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-neutral-800">Terminate Employee</h3>
            <p className="text-sm text-neutral-400 mt-0.5">{displayName}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-lg leading-none">✕</button>
        </div>

        {step === 'form' ? (
          <>
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

              {/* Termination type */}
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Reason for Termination</label>
                <select value={form.terminationType} onChange={e => set('terminationType', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none">
                  {TERMINATION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Last day worked */}
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Last Day Worked</label>
                <input type="date" value={form.lastDayWorked} onChange={e => set('lastDayWorked', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none" />
                <p className="text-xs text-neutral-400 mt-1">Needed for W-2 preparation and state unemployment filings.</p>
              </div>

              {/* Internal note */}
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">
                  Internal Note <span className="text-neutral-300 font-normal">(optional)</span>
                </label>
                <textarea
                  value={form.terminationNote}
                  onChange={e => set('terminationNote', e.target.value)}
                  rows={2}
                  placeholder="Context for HR records only — not visible to employee."
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none resize-none"
                />
              </div>

              {/* Rehire eligible */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-neutral-700">Eligible for Rehire</p>
                  <p className="text-xs text-neutral-400 mt-0.5">Kept on file for future reference</p>
                </div>
                <Toggle field="rehireEligible" />
              </div>

              {!form.rehireEligible && (
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">
                    Reason Not Eligible <span className="text-neutral-300 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={form.rehireNote}
                    onChange={e => set('rehireNote', e.target.value)}
                    placeholder="e.g. Policy violation, attendance..."
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
                  />
                </div>
              )}

              {/* Final pay section */}
              <div className="border-t border-neutral-100 pt-4 space-y-3">
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Final Pay</p>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-neutral-700">Final Paycheck Issued</p>
                    <p className="text-xs text-neutral-400 mt-0.5">Federal law requires timely final pay</p>
                  </div>
                  <Toggle field="finalPayProcessed" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">
                    PTO Payout <span className="text-neutral-300 font-normal">(if applicable)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-sm text-neutral-400">$</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={form.ptoPayout}
                      onChange={e => set('ptoPayout', e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">PTO payout rules vary by state — confirm with your payroll provider.</p>
                </div>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-between shrink-0">
              <button onClick={onClose} className="text-sm text-neutral-500 hover:text-neutral-700 py-2">Cancel</button>
              <button
                onClick={() => setStep('confirm')}
                className="px-5 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Review &amp; Confirm
              </button>
            </div>
          </>
        ) : (
          /* ── Confirm step ────────────────────────────────────────────────── */
          <>
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-red-700 mb-1">This action cannot be undone.</p>
                <p className="text-sm text-red-600">
                  {displayName} will immediately lose portal and POS access. All pay, timesheet, and commission history is preserved for legal and tax purposes.
                </p>
              </div>

              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Employee</span>
                  <span className="font-medium text-neutral-800">{displayName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Reason</span>
                  <span className="font-medium text-neutral-800 capitalize">{form.terminationType.replace('_', ' ')}</span>
                </div>
                {form.lastDayWorked && (
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Last Day</span>
                    <span className="font-medium text-neutral-800">
                      {new Date(form.lastDayWorked + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-neutral-500">Rehire Eligible</span>
                  <span className={`font-medium ${form.rehireEligible ? 'text-emerald-600' : 'text-red-600'}`}>
                    {form.rehireEligible ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Final Pay Issued</span>
                  <span className={`font-medium ${form.finalPayProcessed ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {form.finalPayProcessed ? 'Yes' : 'Not yet'}
                  </span>
                </div>
                {parseFloat(form.ptoPayout) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-neutral-500">PTO Payout</span>
                    <span className="font-medium text-neutral-800">${parseFloat(form.ptoPayout).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-between shrink-0">
              <button onClick={() => setStep('form')} disabled={saving} className="text-sm text-neutral-500 hover:text-neutral-700 py-2">
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={saving}
                className="px-5 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Processing…' : 'Confirm Termination'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeDirectory;
